import { BaseCellProps } from "@components/spreadsheet/cells";
import { Button } from "@design-system/Button/Button";
import { Menu } from "@design-system/Menu/Menu.lib";
import type {
    MigrationResolutionAction,
    MigrationResolutionOption,
} from "@components/accounting/migrationRowResolution";
import { ArrowDownIcon } from "@icons";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import spreadsheetStyles from "@components/spreadsheet/SpreadsheetGrid.module.scss";
import styles from "./MigrationFileUploadTaskBody.module.scss";
import { readMigrationActionRow } from "./migrationActionRow.lib";

const ResolutionMenu = ({
    options,
    disabled,
    onSelect,
}: {
    options: MigrationResolutionOption[];
    disabled?: boolean;
    onSelect: (action: MigrationResolutionAction) => void;
}) => (
    <Menu.Root
        disabled={disabled}
        trigger={
            <Button
                variant="primary"
                size="sm"
                iconOnly
                aria-label="Resolution actions"
            >
                <ArrowDownIcon width={14} height={14} />
            </Button>
        }
    >
        {options.map((option) => (
            <Menu.Item
                key={`${option.action.type}-${option.label}`}
                onClick={() => onSelect(option.action)}
            >
                {option.label}
            </Menu.Item>
        ))}
    </Menu.Root>
);

export const MigrationActionCellContent = ({ row }: BaseCellProps) => {
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const {
        documentId,
        disabled: isDisabled,
        resolutionOptions,
        onResolution,
        onRemove,
    } = readMigrationActionRow(row);

    if (documentId == null) {
        return null;
    }

    const handleResolution = (action: MigrationResolutionAction) => {
        if (refuseIfViewOnly()) {
            return;
        }
        onResolution?.(action);
    };

    const handleRemove = () => {
        if (refuseIfViewOnly()) {
            return;
        }
        onRemove?.(documentId);
    };

    return (
        <div
            className={styles.actionCell}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
        >
            {resolutionOptions.length > 0 && onResolution ? (
                <ResolutionMenu
                    options={resolutionOptions}
                    disabled={isDisabled}
                    onSelect={handleResolution}
                />
            ) : null}
            <Button
                variant="primary"
                size="md"
                className={spreadsheetStyles.showOnRowHover}
                disabled={isDisabled}
                onClick={handleRemove}
            >
                Remove
            </Button>
        </div>
    );
};
